import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AdminAuthResponse,
  IAdmin,
  adminLogin as adminLoginService,
  loadAdminProfile,
} from '../services/admin-auth';
import { deleteCookie, setCookie, getCookie } from '../services';
import { ADMIN_ACCESS_TOKEN_KEY } from '@/constants';

interface AdminAuthContextState {
  admin: IAdmin | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;

  login(
    email: string,
    password: string
  ): Promise<{ success: boolean; data?: AdminAuthResponse; message?: string }>;
  refreshAdmin(): Promise<void>;
  logout(): void;
}

const AdminAuthContext = createContext<AdminAuthContextState | undefined>(
  undefined
);

export const useAdminAuth = (): AdminAuthContextState => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return ctx;
};

export const AdminAuthProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [admin, setAdmin] = useState<IAdmin | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = Boolean(accessToken);

  const persistAccessToken = (token: string | null) => {
    setAccessToken(token);
    token
      ? setCookie(ADMIN_ACCESS_TOKEN_KEY, token, 7)
      : deleteCookie(ADMIN_ACCESS_TOKEN_KEY);
  };

  const persistAdmin = (adminData: IAdmin | null) => {
    if (adminData) {
      setCookie(
        'admin',
        JSON.stringify({
          id: adminData.id,
          email: adminData.email,
          firstName: adminData.firstName,
          lastName: adminData.lastName,
          role: adminData.role,
        }),
        7
      );
      setAdmin(adminData);
    } else {
      deleteCookie('admin');
      setAdmin(null);
    }
  };

  const resetAuth = useCallback(() => {
    setAdmin(null);
    persistAccessToken(null);
    persistAdmin(null);
  }, []);

  const refreshAdmin = useCallback(async () => {
    if (!accessToken) return;

    const dummyRefresh = async () => accessToken;
    const response = await loadAdminProfile(accessToken, dummyRefresh);
    if (response?.success && response.data) {
      setAdmin(response.data);
    }
  }, [accessToken]);

  useEffect(() => {
    const bootstrap = async () => {
      const storedAccessToken = getCookie(ADMIN_ACCESS_TOKEN_KEY);

      if (!storedAccessToken) {
        setLoading(false);
        return;
      }

      persistAccessToken(storedAccessToken);

      const storedAdmin = getCookie('admin');
      if (storedAdmin) {
        try {
          setAdmin(JSON.parse(storedAdmin));
        } catch {
          // Invalid stored admin, will refresh from API
        }
      }

      await refreshAdmin();
      setLoading(false);
    };

    bootstrap();
  }, [refreshAdmin]);

  const login = useCallback(
    async (
      email: string,
      password: string
    ): Promise<{
      success: boolean;
      data?: AdminAuthResponse;
      message?: string;
    }> => {
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      const response = await adminLoginService(email, password);

      if (!response?.success || !response.data) {
        return {
          success: false,
          message: response?.message || 'Login failed',
        };
      }

      const { admin: adminData, token } = response.data;

      setAdmin(adminData);
      persistAccessToken(token);
      persistAdmin(adminData);

      return {
        success: true,
        data: response.data,
        message: response.message,
      };
    },
    []
  );

  const logout = useCallback(() => {
    resetAuth();
  }, [resetAuth]);

  const value = useMemo<AdminAuthContextState>(
    () => ({
      admin,
      accessToken,
      isAuthenticated,
      loading,
      login,
      refreshAdmin,
      logout,
    }),
    [admin, accessToken, isAuthenticated, loading, login, refreshAdmin, logout]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
